// Component Registry - Maps component names from page JSON to actual React components
import { ComponentType } from 'react';
import dynamic from 'next/dynamic';

// ====== DYNAMICALLY IMPORTED COMPONENTS ======
// These are loaded on-demand to reduce initial bundle size

// Heavy components - charts/maps using heavy libraries (Highcharts, Recharts, d3-geo)
const CircleChart = dynamic(() => import('@/components/component_astro_crick/CircleChart'), { ssr: false });
const IndiaMap = dynamic(() => import('@/components/component_astro_crick/IndiaMap'), { ssr: false });
const GaugeComparison = dynamic(() => import('@/components/component_astro_crick/GaugeComparison'), { ssr: false });

// Election components - only loaded on election pages
const Electiontable = dynamic(() => import('@/components/component_astro_crick/Electiontable'), { ssr: false });
const ElectionTableTwo = dynamic(() => import('@/components/component_astro_crick/ElectionTableTwo'), { ssr: false });
const TnResultCount = dynamic(() => import('@/components/component_astro_crick/TnResultCount'), { ssr: false });
const TnResultCountCandidate = dynamic(() => import('@/components/component_astro_crick/TnResultCountCandidate'), { ssr: false });

// Video/Audio components - heavy media players
const VideoPlayer = dynamic(() => import('@/components/component_unique/VideoPlayer'), { ssr: false });
const AudioPlayer = dynamic(() => import('@/components/component_unique/AudioPlayer'), { ssr: false });
const BigVideonews = dynamic(() => import('@/components/component_unique/BigVideonews'), { ssr: false });
const VerticalBigVideoNews = dynamic(() => import('@/components/component_vertical/VerticalBigVideoNews').then(m => ({ default: m.VerticalBigVideoNews })), { ssr: false });
const VerticalBigVideoNewsBlack = dynamic(() => import('@/components/component_vertical/VerticalBigVideoNewsBlack'), { ssr: false });
const VerticalMediumVideoNews = dynamic(() => import('@/components/component_vertical/VerticalMediumVideoNews').then(m => ({ default: m.VerticalMediumVideoNews })), { ssr: false });
const VerticalMediumVideoHeadingNews = dynamic(() => import('@/components/component_vertical/VerticalMediumVideoHeadingNews').then(m => ({ default: m.VerticalMediumVideoHeadingNews })), { ssr: false });
const VerticalBigVideoCategoryNews = dynamic(() => import('@/components/component_vertical/VerticalBigVideoCategoryNews'), { ssr: false });
const HorizontalBigVideoCategoryNews = dynamic(() => import('@/components/component_horizontal/HorizontalBigVideoCategoryNews'), { ssr: false });
const HorizontalBigVideoCategoryNewsBlack = dynamic(() => import('@/components/component_horizontal/HorizontalBigVideoCategoryNewsBlack'), { ssr: false });
const HorizontalVideoNews = dynamic(() => import('@/components/component_horizontal/HorizontalVideoNews'), { ssr: false });
const HorizontalSmallVideoRightNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallVideoRightNews'), { ssr: false });

// Form components - only on specific pages
const Form = dynamic(() => import('@/components/component_form/Form'));
const CompanyForm = dynamic(() => import('@/components/component_form/CompanyForm'));
const EditProfileForm = dynamic(() => import('@/components/component_form/EditprofileForm'));
const NewsPaperfeedbackForm = dynamic(() => import('@/components/component_form/NewsPaperfeedbackForm'));
const LoginRegisterForm = dynamic(() => import('@/components/component_form/LoginRegisterForm'));
const OTPForm = dynamic(() => import('@/components/component_form/OTPForm'));
const ReadFullForm = dynamic(() => import('@/components/component_form/ReadFullForm'));
const PollForm = dynamic(() => import('@/components/component_form/PollForm'));
const ChipsForm = dynamic(() => import('@/components/component_form/ChipsForm'));
const EmailForm = dynamic(() => import('@/components/component_form/email'));
const ChipsFormIcon = dynamic(() => import('@/components/component_form/ChipsFormIcon'));

// Slider component - SSR enabled for instant render (no browser APIs during render)
const Slider = dynamic(() => import('@/components/component_unique/Slider'));
const PopupSlider = dynamic(() => import('@/components/component_unique/PopupSlider'), { ssr: false });
const VerticalNewsSlider = dynamic(() => import('@/components/component_astro_crick/VerticalNewsSlider'), { ssr: false });

// Cricket/Sports components - specialized pages only
const SportsScroreCardHor = dynamic(() => import('@/components/component_astro_crick/SportsScroreCardHor'));
const SportsScroreCardVert = dynamic(() => import('@/components/component_astro_crick/SportsScroreCardVert'));
const IplWindRange = dynamic(() => import('@/components/component_astro_crick/IplWindRange'));
const TeamPointsTable = dynamic(() => import('@/components/component_astro_crick/TeamPointsTable'));
const CricketVoteLevel = dynamic(() => import('@/components/component_astro_crick/CricketVoteLevel'));
const CricketQuestion = dynamic(() => import('@/components/component_astro_crick/CricketQuestion'));
const TeamPlayersPointsTable = dynamic(() => import('@/components/component_astro_crick/TeamPlayersPointsTable'));
const BowlerScoreTable = dynamic(() => import('@/components/component_astro_crick/BowlerScoreTable'));
const CricketComentryOver = dynamic(() => import('@/components/component_astro_crick/CricketComentryOver'));
const HorizontalCricketCommentryBallNews = dynamic(() => import('@/components/component_horizontal/HorizontalCricketCommentryBallNews'));
const ScoreBoardWithTag = dynamic(() => import('@/components/component_astro_crick/ScoreBoardWithTag'));

// Astrology components - specialized pages only
const Astrology = dynamic(() => import('@/components/component_astro_crick/Astrology'));
const AtrologyNewsImageAdd = dynamic(() => import('@/components/component_astro_crick/AtrologyNewsImageAdd'));
const AstroNumbersGrid = dynamic(() => import('@/components/component_astro_crick/AstroNumbersGrid'));
const AstrologyTab = dynamic(() => import('@/components/component_astro_crick/AstrologyTab'));
const AstroQuestion = dynamic(() => import('@/components/component_astro_crick/AstroQuestion'));
const AstroWithOneSideText = dynamic(() => import('@/components/component_astro_crick/AstroWithOneSideText'));
const AstrologySection = dynamic(() => import('@/components/component_astro_crick/AstrologySection'));

// Story/special components
const Story = dynamic(() => import('@/components/component_astro_crick/story'));
const VerticalStoryImageCategoryNews = dynamic(() => import('@/components/component_vertical/VerticalStoryImageCategoryNews').then(m => ({ default: m.VerticalStoryImageCategoryNews })));
const VerticalStoryImageDateNews = dynamic(() => import('@/components/component_vertical/VerticalStoryImageDateNews').then(m => ({ default: m.VerticalStoryImageDateNews })));
const VerticalBigStoryImageDateNews = dynamic(() => import('@/components/component_vertical/VerticalBigStoryImageDateNews').then(m => ({ default: m.VerticalBigStoryImageDateNews })));
const VerticalCategoryNewsSmall = dynamic(() => import('@/components/component_vertical/VerticalCategoryNewsSmall').then(m => ({ default: m.VerticalCategoryNewsSmall })));
// Profile/Auth components
const ProfileIconMenu = dynamic(() => import('@/components/component_unique/ProfileIconMenu'));
const SubscribeDinamani = dynamic(() => import('@/components/component_unique/SubscribeDinamani'));
const BuyDinamani = dynamic(() => import('@/components/component_unique/BuyDinamani'));


// ====== END DYNAMICALLY IMPORTED COMPONENTS ======

// ====== CRITICAL PATH COMPONENTS - Keep static for fast initial render ======
// Only components used in first row of home page should be static

// Primary home page components (first row)
import HorizontalBigImageCategoryNews from '@/components/component_horizontal/HorizontalBigImageCategoryNews';
import { VerticalMediamImageNews } from '@/components/component_vertical/VerticalMediamImageNews';
import HorizontalBigImageCategoryNewsHome from '@/components/component_horizontal/HorizontalBigImageCategoryNewsHome';
import TextWithImage from '@/components/component_unique/TextWithImage';
import { VerticalBigImagesNewsWhite } from '@/components/component_vertical/VerticalBigImagesNewsWhite';
import { VerticalMediumImageNewsBuilder } from '@/components/component_vertical/VerticalMediumImageNewsBuilder';
import VerticalGradientWithPlaybutton from '@/components/component_vertical/VerticalGradientWithPlaybutton';
import HorizontalBigVideoNews from '@/components/component_horizontal/HorizontalBigVideoNews';

// ====== DYNAMIC IMPORTS - Load on demand to reduce initial bundle ======

// Vertical Components
const VerticalBigImagecategoryNews = dynamic(() => import('@/components/component_vertical/VerticalBigImagecategoryNews'));
const VerticalCategoryNews = dynamic(() => import('@/components/component_vertical/VerticalCategoryNews').then(m => ({ default: m.VerticalCategoryNews })));
const VerticalMediamImageCategoryNews = dynamic(() => import('@/components/component_vertical/VerticalMediamImageCategoryNews').then(m => ({ default: m.VerticalMediamImageCategoryNews })));
const VerticalSmallImageCategoryNews = dynamic(() => import('@/components/component_vertical/VerticalSmallImageCategoryNews').then(m => ({ default: m.VerticalSmallImageCategoryNews })));
const VerticalMediamImageDescNews = dynamic(() => import('@/components/component_vertical/VerticalMediamImageDescNews').then(m => ({ default: m.VerticalMediamImageDescNews })));
const VerticalBigImageNews = dynamic(() => import('@/components/component_vertical/VerticalBigImageNews').then(m => ({ default: m.VerticalBigImageNews })));
const VerticalBigImagesNews = dynamic(() => import('@/components/component_vertical/VerticalBigImagesNews').then(m => ({ default: m.VerticalBigImagesNews })));
const VerticalMediamImageDescNews2 = dynamic(() => import('@/components/component_vertical/VerticalMediamImageDescNews2').then(m => ({ default: m.VerticalMediamImageDescNews2 })));
const VerticalSmallImageNews = dynamic(() => import('@/components/component_vertical/VerticalSmallImageNews').then(m => ({ default: m.VerticalSmallImageNews })));
const VerticalSmallImagesNews = dynamic(() => import('@/components/component_vertical/VerticalSmallImagesNews').then(m => ({ default: m.VerticalSmallImagesNews })));
const VerticalheadingBigImageNews = dynamic(() => import('@/components/component_vertical/VerticalheadingBigImageNews'));
const VerticalMediamRatingNews = dynamic(() => import('@/components/component_vertical/VerticalMediamRatingNews').then(m => ({ default: m.VerticalMediamRatingNews })));
const VerticalMediamTagNews = dynamic(() => import('@/components/component_vertical/VerticalMediamTagNews').then(m => ({ default: m.VerticalMediamTagNews })));
const VerticalMediumImageHeadingNews = dynamic(() => import('@/components/component_vertical/VerticalMediumImageHeadingNews').then(m => ({ default: m.VerticalMediumImageHeadingNews })));
const VerticalBigImagecategoryNewsLive = dynamic(() => import('@/components/component_vertical/VerticalBigImagecategoryNewsLive'));
const VerticalImageGradiantNews = dynamic(() => import('@/components/component_vertical/VerticalImageGradiantNews'));
const VerticalMediumDescNews = dynamic(() => import('@/components/component_vertical/VerticalMediumDescNews').then(m => ({ default: m.VerticalMediumDescNews })));
const VerticalBigImagecategoryNewsbgWhite = dynamic(() => import('@/components/component_vertical/VerticalBigImagecategoryNewsbgWhite'));
const VerticalSmallImagesNewsWhite = dynamic(() => import('@/components/component_vertical/VerticalSmallImagesNewsWhite').then(m => ({ default: m.VerticalSmallImagesNewsWhite })));

// Horizontal Components
const HorizontalBigImageNewsWhite = dynamic(() => import('@/components/component_horizontal/HorizontalBigImageNewsWhite'));
const HorizontalSmallImageCategoryNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallImageCategoryNews'));
const HorizontalSmallRightImageCategoryNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallRightImageCategoryNews'));
const HorizontalSmallCategoryNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallCategoryNews'));
const HorizontalSmallNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallNews'));
const HorizontalBigImageNews = dynamic(() => import('@/components/component_horizontal/HorizontalBigImageNews'));
const HorizontalSmallImageHeadingNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallImageHeadingNews'));
const HorizontalSideMostview = dynamic(() => import('@/components/component_horizontal/horizontalSideMostview'));
const HorizontalCommentsNews = dynamic(() => import('@/components/component_horizontal/HorizontalCommentsNews'));
const HorizontalCommentsIconsNews = dynamic(() => import('@/components/component_horizontal/HorizontalCommentsIconsNews'));
const HorizontalDateNews = dynamic(() => import('@/components/component_horizontal/HorizontalDateNews'));
const HorizontalSmallImageNews = dynamic(() => import('@/components/component_horizontal/HorizontalSmallImageNews'));
const HorizontalBigImageAuthorNews = dynamic(() => import('@/components/component_horizontal/HorizontalBigImageAuthorNews'));
const HorizontalBigCategorynews = dynamic(() => import('@/components/component_horizontal/HorizontalBigCategorynews'));
const AuthorNameWithImage = dynamic(() => import('@/components/component_horizontal/AuthorNameWithImage'));

// Unique Components
const FollowUsFacebook = dynamic(() => import('@/components/component_unique/FollowUsFacebook'));
const EmojiRating = dynamic(() => import('@/components/component_unique/EmojiRating'));
const Divider = dynamic(() => import('@/components/component_unique/divider'));
const SocialMediaButtons = dynamic(() => import('@/components/component_unique/SocialMediaButtons'));
const DistrictNameImage = dynamic(() => import('@/components/component_unique/DistrictNameImage'));
const Searchbar = dynamic(() => import('@/components/component_unique/searchbar'));
const SearchNoResult = dynamic(() => import('@/components/component_unique/SearchNoResult'));
const FollowUsTwitter = dynamic(() => import('@/components/component_unique/FollowUsTwitter'));
const NewsWidget = dynamic(() => import('@/components/component_unique/NewsWidget'));
const PaperHeader = dynamic(() => import('@/components/component_unique/PaperHeader'));

// Other components
const PollResult = dynamic(() => import('@/components/component_astro_crick/PollResult'));
const SendComment = dynamic(() => import('@/components/component_astro_crick/SendComment'));
const LatestCard = dynamic(() => import('@/components/component_astro_crick/LatestCard'));
const IconWithtext = dynamic(() => import('@/components/component_astro_crick/IconWithtext'));
const ArticleText2 = dynamic(() => import('@/components/component_astro_crick/ArticleText2'));
const BigNewsWithicon = dynamic(() => import('@/components/component_astro_crick/BigNewsWithicon'));
const OnesideText = dynamic(() => import('@/components/component_astro_crick/OnesideText'));
const Pagination = dynamic(() => import('@/components/component_astro_crick/pagination'));
const RecentNews = dynamic(() => import('@/components/component_astro_crick/RecentNews'));
const TextWithBigImageAndIcon = dynamic(() => import('@/components/component_astro_crick/TextWithBigImageAndIcon'));
const WhiteMenuBar = dynamic(() => import('@/components/component_astro_crick/WhiteMenuBar'));
const WhiteMenuBar2 = dynamic(() => import('@/components/component_astro_crick/WhiteMenuBar2'));
const ImageWithText = dynamic(() => import('@/components/ui/imagewithtext'));
const ImageText = dynamic(() => import('@/components/component_vertical/imagetext'));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const componentRegistry: Record<string, ComponentType<any>> = {
  // Vertical Components
  'VerticalBigImagecategoryNews': VerticalBigImagecategoryNews,
  'VerticalBigImageCategoryNews': VerticalBigImagecategoryNews, // alias with different casing
  'VerticalMediamImageNews': VerticalMediamImageNews,
  'VerticalMediumImageNews': VerticalMediamImageNews, // alias
  'VerticalCategoryNews': VerticalCategoryNews,
  'VerticalMediamImageCategoryNews': VerticalMediamImageCategoryNews,
  'VerticalMediumImageCategoryNews': VerticalMediamImageCategoryNews, // alias
  'VerticalSmallImageCategoryNews': VerticalSmallImageCategoryNews,
  'VerticalMediamImageDescNews': VerticalMediamImageDescNews,
  'VerticalMediumImageDescNews': VerticalMediamImageDescNews, // alias with correct spelling
  'VerticalMediumIDescNews': VerticalMediumDescNews, // alias from JSON
  'VerticalBigVideoNews': VerticalBigVideoNews,
  'VerticalBigVideoNewsBlack': VerticalBigVideoNewsBlack,
  'VerticalStoryImageCategoryNews': VerticalStoryImageCategoryNews,
  'VerticalBigImageNews': VerticalBigImageNews,
  'VerticalStoryImageDateNews': VerticalStoryImageDateNews,
  'VerticalBigStoryImageDateNews': VerticalBigStoryImageDateNews,
  'VerticalCategoryNewsSmall': VerticalCategoryNewsSmall,
  'VerticalBigImagesNews': VerticalBigImagesNews,
  'VerticalMediamImageDescNews2': VerticalMediamImageDescNews2,
  'VerticalSmallImageNews': VerticalSmallImageNews,
  'VerticalSmallImagesNews': VerticalSmallImagesNews,
  'VerticalheadingBigImageNews': VerticalheadingBigImageNews,
  'VerticalMediumVideoNews': VerticalMediumVideoNews,
  'VerticalBigVideoCategoryNews': VerticalBigVideoCategoryNews,
  'VerticalMediamRatingNews': VerticalMediamRatingNews,
  'VerticalMediamTagNews': VerticalMediamTagNews,
  'VerticalMediumVideoHeadingNews': VerticalMediumVideoHeadingNews,
  'VerticalMediumImageHeadingNews': VerticalMediumImageHeadingNews,
  'VerticalBigImagecategoryNewsLive': VerticalBigImagecategoryNewsLive,
  'VerticalBigImageCategoryNewsBG': VerticalBigImagecategoryNews, // alias for background variant
  'VerticalImageGradiantNews': VerticalImageGradiantNews,
  'VerticalBigImagecategoryNewsbgWhite': VerticalBigImagecategoryNewsbgWhite,
  'VerticalSmallImagesNewsWhite': VerticalSmallImagesNewsWhite,
  'VerticalBigImagesNewsWhite': VerticalBigImagesNewsWhite,
  'VerticalMediumImageNewsBuilder':VerticalMediumImageNewsBuilder,
  'VerticalGradientWithPlaybutton':VerticalGradientWithPlaybutton,
  // Horizontal Components
  'HorizontalBigImageCategoryNews': HorizontalBigImageCategoryNews,
  'HorizontalCategoryNews': HorizontalSmallCategoryNews, // alias for missing component
  'HorizontalSmallImageCategoryNews': HorizontalSmallImageCategoryNews,
  'HorizontalSmallRightImageCategoryNews': HorizontalSmallRightImageCategoryNews,
  'HorizontalSmallCategoryNews': HorizontalSmallCategoryNews,
  'HorizontalSmallNews': HorizontalSmallNews,
  'HorizontalBigImageNews': HorizontalBigImageNews,
  'HorizontalSmallImageHeadingNews': HorizontalSmallImageHeadingNews,
  'HorizontalBigVideoCategoryNews': HorizontalBigVideoCategoryNews,
  'HorizontalBigVideoCategoryNewsBlack': HorizontalBigVideoCategoryNewsBlack,
  'horizontalSideMostview': HorizontalSideMostview,
  'HorizontalCommentsNews': HorizontalCommentsNews,
  'HorizontalSmallVideoRightNews': HorizontalSmallVideoRightNews,
  'HorizontalCommentsIconsNews': HorizontalCommentsIconsNews,
  'HorizontalDateNews': HorizontalDateNews,
  'HorizontalVideoNews': HorizontalVideoNews,
  'HorizontalCricketCommentryBallNews': HorizontalCricketCommentryBallNews,
  'HorizontalSmallImageNews': HorizontalSmallImageNews,
  'HorizontalBigImageAuthorNews': HorizontalBigImageAuthorNews,
  'HorizontalBignews' : HorizontalBigCategorynews,
  'HorizontalBigCategorynews' : HorizontalBigCategorynews,
  'HorizontalBigImageCategoryNewsHome':HorizontalBigImageCategoryNewsHome,
  'HorizontalBigImageNewsWhite':HorizontalBigImageNewsWhite,
  'HorizontalSmallImageCategoryNewsWhite':HorizontalSmallImageCategoryNews,
  "HorizontalBigVideoNews" : HorizontalBigVideoNews,
  // Form Components
  'Form': Form,
  'CompanyForm': CompanyForm,
  'EditProfileForm': EditProfileForm,
  'NewsPaperfeedbackForm': NewsPaperfeedbackForm,
  'LoginRegisterForm': LoginRegisterForm,
  'OTPForm': OTPForm,
  'ReadFullForm': ReadFullForm,
  'PollForm': PollForm,
  'ChipsForm': ChipsForm,
  'EmailForm': EmailForm,
  'ChipsFormIcon': ChipsFormIcon,
  'AstrologySection': AstrologySection,
  // Unique Components
  'Slider': Slider,
  'FollowUsFacebook': FollowUsFacebook,
  'VideoPlayer': VideoPlayer,
  'AudioPlayer': AudioPlayer,
  'EmojiRating': EmojiRating,
  'Divider': Divider,
  'SocialMediaButtons': SocialMediaButtons,
  'DistrictNameImage': DistrictNameImage,
  'Searchbar': Searchbar,
  'SearchNoResult': SearchNoResult,
  'ProfileIconMenu': ProfileIconMenu,
  'SubscribeDinamani': SubscribeDinamani,
  'BuyDinamani': BuyDinamani,
  'PopupSlider': PopupSlider,
  'FollowUsTwitter': FollowUsTwitter,
  'BigVideonews': BigVideonews,
  'NewsWidget': NewsWidget,
  'PaperHeader': PaperHeader,
  'AuthorNameWithImage':AuthorNameWithImage,
  'ImageWithText':ImageWithText,
  'ImageText':ImageText,
  'videoPlayer':VideoPlayer,
  'TextWithImage':TextWithImage,
  
  // Astro/Cricket Components
  'SportsScroreCardHor': SportsScroreCardHor,
  'Astrology': Astrology,
  'astrologyWidget': Astrology, // alias for widget variant
  'AtrologyNewsImageAdd': AtrologyNewsImageAdd,
  'PollResult': PollResult,
  'SportsScroreCardVert': SportsScroreCardVert,
  'IplWindRange': IplWindRange,
  'TeamPointsTable': TeamPointsTable,
  'CricketVoteLevel': CricketVoteLevel,
  'CricketQuestion': CricketQuestion,
  'TeamPlayersPointsTable': TeamPlayersPointsTable,
  'BowlerScoreTable': BowlerScoreTable,
  'CricketComentryOver': CricketComentryOver,
  'SendComment': SendComment,
  'TnResultCount': TnResultCount,
  'Electiontable': Electiontable,
  'TnResultCountCandidate': TnResultCountCandidate,
  'CircleChart': CircleChart,
  'LatestCard': LatestCard,
  'IndiaMap': IndiaMap,
  'ElectionTableTwo': ElectionTableTwo,
  'GaugeComparison': GaugeComparison,
  'IconWithtext': IconWithtext,
  'AstroNumbersGrid': AstroNumbersGrid,
  'ArticleText2': ArticleText2,
  'BigNewsWithicon': BigNewsWithicon,
  'OnesideText': OnesideText,
  'Pagination': Pagination,
  'RecentNews': RecentNews,
  'Story': Story,
  'TextWithBigImageAndIcon': TextWithBigImageAndIcon,
  'WhiteMenuBar': WhiteMenuBar,
  'VerticalNewsSlider': VerticalNewsSlider,
  'WhiteMenuBar2': WhiteMenuBar2,
  'AstrologyTab': AstrologyTab,
  'AstroQuestion': AstroQuestion,
  'AstroWithOneSideText': AstroWithOneSideText,
  'ScoreBoardWithTag': ScoreBoardWithTag,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getComponent(componentType: string): ComponentType<any> | null {
  return componentRegistry[componentType] || null;
}
